import React, { useState } from 'react';
import componentStyles from './propBetRecommendation.module.css';
import { formatPropMarketTypeLabel } from '../../utils/columnUtils';

// Maps scouting confidence level to badge background (same palette as prop cards).
export const getConfidenceColor = (confidence) => {
    switch (confidence?.toLowerCase()) {
        case 'high':
            return '#00ff00'; // Green
        case 'medium':
            return '#808000'; // Olive
        case 'low':
            return '#ff0000'; // Red
        default:
            return '#808000'; // Default to olive
    }
};

// Uppercase label for the confidence badge.
export const getConfidenceText = (confidence) => {
    return confidence?.toUpperCase() || 'MEDIUM';
};

export const PropBetRecommendationPlayer = ({player, propBets, isOpen, onToggle}) => { 
    const playerId = `player-${player.replace(/\s+/g, '-').toLowerCase()}`;
    const validPropBets = propBets.filter(({ propBetMarketTypeRecommendedBet }) => {
        const recommendedPick = String(propBetMarketTypeRecommendedBet || '').trim();
        return recommendedPick !== '' && recommendedPick.toLowerCase() !== 'n/a';
    });

    const propBetRows = validPropBets.map((ppd) => {
        return <PropBetRecommendationPlayerBet 
            key={`${playerId}-${ppd.propBetMarketType}`}
            propBetData={ppd} 
        />
    });

    if (validPropBets.length === 0) {
        return null;
    }

    return (
        <div className={`${componentStyles.propBetRecommendationPlayer} ${isOpen ? componentStyles.expand : ''}`} key={playerId}>
            <div className={componentStyles.propBetRecommendationPlayerRow}>
                <p className={componentStyles.propBetRecommendationPlayerTitle}>{player}</p>
                <button className={componentStyles.hideShowPlayerButton} onClick={onToggle}>
                    {isOpen ? 'Hide' : 'View'}
                </button>
            </div>
            {isOpen && (
                <div className={componentStyles.propBetRecommendationPlayerBets}>
                    {propBetRows}
                </div>
            )}
        </div>
    )

}

export const PropBetRecommendationPlayerBet = ({propBetData}) => {
    const { propBetMarketType, propBetMarketTypeBetExplanation, propBetMarketTypeRecommendedBet, propBetConfidence, odds } = propBetData;
    const marketLabel = formatPropMarketTypeLabel(propBetMarketType);
    const recommendedBet = String(propBetMarketTypeRecommendedBet || '').toLowerCase();
    const recommendedDirection = recommendedBet.startsWith('over') ? 'over' : recommendedBet.startsWith('under') ? 'under' : null;

    const formatOddsValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return 'N/A';
        }

        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
            if (numericValue >= 2.0) {
                return `+${Math.round((numericValue - 1) * 100)}`;
            }

            if (numericValue > 1.0) {
                return `${Math.round(-100 / (numericValue - 1))}`;
            }
        }

        return String(value);
    };

    const selectedPoint = recommendedDirection === 'over' ? odds?.over_point : recommendedDirection === 'under' ? odds?.under_point : null;
    const selectedOdds = recommendedDirection === 'over' ? odds?.over_odds : recommendedDirection === 'under' ? odds?.under_odds : null;
    const selectedDirectionLabel = recommendedDirection ? `${recommendedDirection.charAt(0).toUpperCase()}${recommendedDirection.slice(1)} Pick` : 'Recommended Pick';
    return (
        <div className={componentStyles.propBetRecommendationPlayerBet}>
            <div className={componentStyles.propBetRecommendationPlayerBetHeader}>
                <span className={componentStyles.pickLabel}>{marketLabel}</span>
                <span className={componentStyles.pickValue}>{propBetMarketTypeRecommendedBet}</span>
                <span
                    className={componentStyles.confidenceBadge}
                    style={{ backgroundColor: getConfidenceColor(propBetConfidence) }}
                >
                    {getConfidenceText(propBetConfidence)}
                </span>
            </div>
            <div className={componentStyles.comparePanel}>
                <div className={componentStyles.compareGrid}>
                    <div className={`${componentStyles.compareCol} ${componentStyles.compareColBest} ${componentStyles.compareColSingle}`}>
                        <div className={componentStyles.compareColLabel}>{selectedDirectionLabel}</div>
                        <div className={componentStyles.compareNumbers}>
                            <span className={componentStyles.lineValue}>
                                {selectedPoint ?? 'N/A'}
                            </span>
                            <span className={componentStyles.oddsValue}>
                                {formatOddsValue(selectedOdds)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div className={componentStyles.propBetRecommendationPlayerBetExplanation}>
                <p>
                    {propBetMarketTypeBetExplanation}
                </p>
            </div>
        </div>
    )
}

const PropBetRecommendation = ({propBetRecommendationData}) => {
    const [searchPlayer, setSearchPlayer] = useState('');
    const [openPlayer, setOpenPlayer] = useState(null);
    const handleSearchPlayer = (e) => {
        setSearchPlayer(e.target.value);
    }
    const displayPropBetRecommendation = () => {
        /*
        {
    "player": "Cole Anthony",
    "propBets": [
        {
            "propBetMarketType": "player_points",
            "propBetMarketTypeBetExplanation": "Anthony is coming off a 26-point play-in game and is the Magic’s instant-offense option off the bench. Boston’s defense is tough, but Anthony’s confidence and shot volume suggest the over as a strong lean if playing 20+ minutes.",
            "propBetMarketTypeRecommendedBet": "Cole Anthony Over 7.5 Points",
            "propBetConfidence": "Medium"
        }
    ]
}
        */
        
        const propBetReport = propBetRecommendationData && (propBetRecommendationData['prop_bet_report'] || propBetRecommendationData)
        const players = propBetReport['players'];
        const playersData = players.filter(({player, propBets}) => {
            if (searchPlayer === '') {
                return propBets.some(({ propBetMarketTypeRecommendedBet }) => {
                    const recommendedPick = String(propBetMarketTypeRecommendedBet || '').trim();
                    return recommendedPick !== '' && recommendedPick.toLowerCase() !== 'n/a';
                });
            }
            
            return player.toLowerCase().includes(searchPlayer.toLowerCase()) && propBets.some(({ propBetMarketTypeRecommendedBet }) => {
                const recommendedPick = String(propBetMarketTypeRecommendedBet || '').trim();
                return recommendedPick !== '' && recommendedPick.toLowerCase() !== 'n/a';
            });
        }).map(({player, propBets}) => {              
            return (   
                <PropBetRecommendationPlayer 
                    key={player}
                    player={player} 
                    propBets={propBets}
                    isOpen={openPlayer === player}
                    onToggle={() => setOpenPlayer(openPlayer === player ? null : player)}
                />
            )
        })

        return (
            <div className={componentStyles.propBetRecommendation}>
                <div className={componentStyles.propBetRecommendationSearch}>
                    <input 
                        type="text" 
                        placeholder="Search player" 
                        onChange={(e) => handleSearchPlayer(e)}
                        value={searchPlayer}
                    />
                </div>
                <div className={componentStyles.propBetRecommendationPlayersList}>
                    {playersData}
                </div>
            </div>
        )
    }

    return (
        <>
            {propBetRecommendationData && displayPropBetRecommendation()}
        </>
    )
}

export default PropBetRecommendation;
